import { Trans, useLingui } from '@lingui/react';
import { Button } from '@startuptools/ui/Button';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { Card } from '@startuptools/ui/Card';
import { IconButton } from '@startuptools/ui/IconButton';
import PreviewIcon from '@mui/icons-material/Preview';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Stack } from '@startuptools/ui/Stack';
import { LabeledText } from '@startuptools/ui/LabeledText';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { ErrorBoundary } from '@startuptools/ui/ErrorBoundary';
import { Alert } from '@startuptools/ui/Alert';
import StopIcon from '@mui/icons-material/Stop';
import { Option } from '@startuptools/ui/Option';
import { Select } from '@startuptools/ui/Select';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { Modal } from '@startuptools/ui/Modal';
import { Dropdown } from '@startuptools/ui/Dropdown';
import { MenuButton } from '@startuptools/ui/MenuButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Menu } from '@startuptools/ui/Menu';
import { MenuItem } from '@startuptools/ui/MenuItem';
import { ListItemDecorator } from '@startuptools/ui/ListItemDecorator';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { Typography } from '@startuptools/ui/Typography';
import { Link } from '@startuptools/ui/Link';
import { EditParticipantDialog } from './Edit';
import { type DisplayDocument } from '@startuptools/business-ui/document/Document';
import { DisplayVesting, DisplayVestingFrequency } from '@startuptools/business-ui/vesting';
import { type DisplayIdentity, Identity } from '@startuptools/business-ui/identity/Identity';
import { PaymentKind, PaymentStatus } from '@startuptools/common/participants';
import { ConfirmDialog } from '@startuptools/ui/ConfirmDialog';
import * as React from 'react';
import { IDKind, JuridicalKind } from '../../../../../../models/identity.model';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';
import { InputState } from '@startuptools/business-ui/identity/types';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import {
  type GqlCompany,
  type GqlIdentity,
  type GqlWarrantProgram,
  type GqlOptionsRegistration,
  GqlWarrantProgramKind,
  type GqlWarrantParticipant,
  type GqlBolagoFile,
} from '../../../../../../graphql/base-types.graphql';
import { useWppDeleteMutation, useWppUpdateMutation } from '../../../../../../graphql/react-operations';
import { type DocumentNode } from 'graphql';
import {
  useCreateOptionsSubscriberRepresentativeMutation,
  useDeleteOptionsSubscriberRepresentativeMutation,
} from './or-subscribers.graphql';
import { VestingKind } from '@startuptools/common/stock-options';
import { WarrantAgreementDocument } from '../../../../../common/components/documents/WarrantAgreementDocument';
import { EndVesting } from './EndVesting';
import { host } from '../../../../../../../environments/environment';
import { BigNumber } from 'bignumber.js';
import { PaymentKindHuman } from '../../../../../../components/payment-kind-human/PaymentKindHuman';

export const Participant = ({
  company,
  participant,
  or,
  warrantProgram,
  refetchQueries,
  oversubscribed,
  navigateToDecisionStep,
}: {
  company: Pick<GqlCompany, 'id' | 'name' | 'allowShortQeso' | 'organizationNumber'>;
  warrantProgram: Pick<
    GqlWarrantProgram,
    'id' | 'name' | 'warrantKind' | 'usesStepper' | 'companyId' | 'warrantStartDate' | 'shareClasses'
  >;
  or: Pick<GqlOptionsRegistration, 'id' | 'description'>;
  refetchQueries: (string | DocumentNode)[];
  oversubscribed: boolean;
  participant: Pick<
    GqlWarrantParticipant,
    | 'id'
    | 'quantity'
    | 'shareClass'
    | 'paymentKind'
    | 'paymentStatus'
    | 'vestingKind'
    | 'vestingCliffDuration'
    | 'vestingDuration'
    | 'vestingFrequency'
    | 'repurchaseVested'
    | 'vestingAcceleration'
    | 'leaveOfAbsence'
    | 'leaveOfAbsenceDays'
    | 'optionsRegistrationId'
    | 'forcedRedemption'
    | 'vestingOccasions'
    | 'claim'
    | 'vestingEndDate'
    | 'vestingLastPotentialDate'
    | 'vestedQuantityOnEndDate'
    | 'createdAt'
    | 'quantityRepurchased'
  > & {
    identity: DisplayIdentity & Pick<GqlIdentity, 'juridicalKind'>;
    latestAgreementDoc?: (DisplayDocument & Pick<GqlBolagoFile, 'isFullySigned'>) | undefined | null;
    representatives: {
      id: string;
      identity: DisplayIdentity;
    }[];
  };
  navigateToDecisionStep: () => void;
}) => {
  const { i18n } = useLingui();

  const [updateWpp, { loading }] = useWppUpdateMutation({ refetchQueries });
  const [dialog, setDialog] = React.useState<'edit' | 'end-vesting' | 'delete' | 'identity' | null>(null);
  // eslint-disable-next-line sut/loading-mutation
  const [createRep] = useCreateOptionsSubscriberRepresentativeMutation({ refetchQueries });
  // eslint-disable-next-line sut/loading-mutation
  const [deleteRep] = useDeleteOptionsSubscriberRepresentativeMutation({ refetchQueries });
  // eslint-disable-next-line sut/loading-mutation
  const [deleteParticipant] = useWppDeleteMutation({ refetchQueries });

  return (
    <Card>
      <Stack gap={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <Identity identity={participant.identity} />
            <LabeledText
              tooltip={
                participant.vestingEndDate ? (
                  <Trans
                    id="Vesting ends on {date}. The number below reflects how many shares will have vested on that day."
                    values={{ date: <DateFormat locale={i18n.locale}>{participant.vestingEndDate}</DateFormat> }}
                  />
                ) : null
              }
              textColor={oversubscribed ? 'danger.500' : undefined}
              label={
                warrantProgram.warrantKind === GqlWarrantProgramKind.Nice ? (
                  <Trans id="Convertibles" />
                ) : (
                  <Trans id="Options" />
                )
              }
            >
              {new BigNumber(participant.quantityRepurchased ?? '0').gt(0) ? (
                <>
                  <Typography sx={{ textDecoration: 'line-through' }}>
                    <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                      {participant.quantity}
                    </NumberFormat>
                  </Typography>{' '}
                  <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                    {participant.quantityRepurchased}
                  </NumberFormat>
                </>
              ) : (
                <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                  {participant.quantity}
                </NumberFormat>
              )}
            </LabeledText>
            {![GqlWarrantProgramKind.Nice, GqlWarrantProgramKind.Wise].includes(warrantProgram.warrantKind) && (
              <LabeledText label={<Trans id="Share class" />}>{participant.shareClass?.name}</LabeledText>
            )}
            {warrantProgram.usesStepper && warrantProgram.warrantKind !== GqlWarrantProgramKind.Employee && (
              <>
                <LabeledText label={<Trans id="Payment method" />}>
                  <PaymentKindHuman>{participant.paymentKind}</PaymentKindHuman>
                </LabeledText>

                <Select
                  value={participant.paymentStatus.toString()}
                  onChange={async (_event, newValue) => {
                    await updateWpp({
                      variables: {
                        companyId: company.id,
                        input: {
                          id: participant.id,
                          paymentStatus: parseInt(newValue ?? '0'),
                        },
                      },
                    });
                  }}
                >
                  <Option value={PaymentStatus.Paid.toString()}>
                    <Trans id="Has paid" />
                  </Option>
                  <Option value={PaymentStatus.WaitingForPayment.toString()}>
                    <Trans id="Waiting for payment" />
                  </Option>
                  <Option value={PaymentStatus.NotPaid.toString()}>
                    <Trans id="Not paid in time" />
                  </Option>
                </Select>
              </>
            )}
          </Stack>

          <Dropdown>
            <MenuButton slots={{ root: IconButton }} slotProps={{ root: { variant: 'outlined', color: 'neutral' } }}>
              <MoreVertIcon />
            </MenuButton>
            <Menu placement="bottom-end">
              <MenuItem>
                <ListItemDecorator>
                  <PreviewIcon />
                </ListItemDecorator>
                <Link
                  href={`${host.shPortalUrl}/app/options/detail?id=${participant.id}`}
                  target="_blank"
                  sx={{ color: 'inherit' }}
                >
                  <Trans id="View as subscriber" />
                </Link>
              </MenuItem>

              {participant.vestingKind !== VestingKind.None && (
                <MenuItem onClick={() => setDialog('end-vesting')}>
                  <ListItemDecorator>
                    <StopIcon />
                  </ListItemDecorator>
                  <Trans id="End vesting" />
                </MenuItem>
              )}
              <MenuItem onClick={() => setDialog('edit')}>
                <ListItemDecorator>
                  <EditIcon />
                </ListItemDecorator>
                <Trans id="Edit" />
              </MenuItem>
              <MenuItem onClick={() => setDialog('delete')} color="danger">
                <ListItemDecorator>
                  <DeleteIcon />
                </ListItemDecorator>
                <Trans id="Delete" />
              </MenuItem>
            </Menu>
          </Dropdown>
        </Stack>
        {![GqlWarrantProgramKind.Nice, GqlWarrantProgramKind.Wise].includes(warrantProgram.warrantKind) && (
          <Stack direction="row" spacing={1}>
            <LabeledText label={<Trans id="Vesting connected to" />}>
              <DisplayVesting kind={participant.vestingKind} />
            </LabeledText>
            <LabeledText label={<Trans id="Forced redemption" />}>
              {participant.forcedRedemption ? <Trans id="Yes" /> : <Trans id="No" />}
            </LabeledText>
            {participant.vestingKind !== VestingKind.None && (
              <>
                {participant.vestingKind !== VestingKind.Custom && (
                  <>
                    <LabeledText label={<Trans id="Vesting frequency" />}>
                      {<DisplayVestingFrequency frequency={participant.vestingFrequency} />}
                    </LabeledText>
                    <LabeledText label={<Trans id="Vesting duration" />}>{participant.vestingDuration}</LabeledText>
                    <LabeledText label={<Trans id="Cliff" />}>{participant.vestingCliffDuration}</LabeledText>
                  </>
                )}

                <LabeledText label={<Trans id="Repurchase of vested" />}>
                  {participant.repurchaseVested ? <Trans id="Yes" /> : <Trans id="No" />}
                </LabeledText>
                <LabeledText label={<Trans id="Accelerated vesting" />}>
                  {participant.vestingAcceleration ? <Trans id="Yes" /> : <Trans id="No" />}
                </LabeledText>
                {participant.vestingEndDate && (
                  <LabeledText label={<Trans id="Vesting end date" />}>
                    <DateFormat locale={i18n.locale}>{participant.vestingEndDate}</DateFormat>
                  </LabeledText>
                )}
                {[GqlWarrantProgramKind.Employee, GqlWarrantProgramKind.Warrant].includes(warrantProgram.warrantKind) &&
                  participant.vestingKind !== VestingKind.Custom &&
                  participant.leaveOfAbsence && (
                    <LabeledText label={<Trans id="Leave of absence" />}>
                      <Trans id="{amount} days" values={{ amount: participant.leaveOfAbsenceDays }} />
                    </LabeledText>
                  )}
              </>
            )}
          </Stack>
        )}

        {participant.identity.juridicalKind === JuridicalKind.Company && (
          <Stack spacing={1}>
            <ButtonGroup>
              <Button
                variant="outlined"
                onClick={() => {
                  setDialog('identity');
                }}
              >
                <Trans id="Add representative" />
              </Button>
            </ButtonGroup>

            <Stack direction="row" gap={2}>
              {participant.representatives.map(rep => {
                return (
                  <Card key={rep.id}>
                    <Stack direction="row" key={rep.id} gap={1}>
                      <Identity identity={rep.identity} />
                      <IconButton
                        color="danger"
                        onClick={async () => {
                          await deleteRep({ variables: { companyId: company.id, id: rep.id } });
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          </Stack>
        )}
        <WarrantAgreementDocument
          company={company}
          warrantProgram={warrantProgram}
          optionsRegistration={or}
          participant={participant}
          navigateToDecisionStep={navigateToDecisionStep}
        />
      </Stack>
      {dialog === 'identity' && (
        <AddIdentity
          headline={<Trans id="Add representative" />}
          juridicalKinds={[JuridicalKind.Person]}
          availableInputStates={{
            [InputState.System]: false,
            [InputState.CompanyBoard]: participant.identity.identityNumberKind === IDKind.SwedishOrgNumber,
            [InputState.Swedish]: true,
            [InputState.Foreign]: true,
          }}
          companyBoardOrgNumber={participant.identity.identityNumber}
          handleCreate={async identity => {
            await createRep({
              variables: {
                companyId: company.id,
                input: {
                  identityInput: identityToInput(identity),
                  warrantParticipantId: participant.id,
                },
              },
            });
            setDialog(null);
          }}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'edit' && (
        <ErrorBoundary showModal onReset={() => setDialog(null)}>
          <EditParticipantDialog
            infoBanner={
              Boolean(participant.latestAgreementDoc?.uploaded) && (
                <Alert color="primary">
                  <Trans id="This subscriber has a custom document attached to it. The configuration below should match the text of the agreement." />
                </Alert>
              )
            }
            optionRegistrationId={or.id}
            paymentKind={PaymentKind.Cash}
            closeDialog={() => {
              setDialog(null);
            }}
            identity={participant.identity}
            participant={participant}
            warrantProgram={warrantProgram}
            company={company}
            refetchQueries={refetchQueries}
          />
        </ErrorBoundary>
      )}
      {dialog === 'end-vesting' && (
        <ErrorBoundary showModal>
          <Modal open onClose={() => setDialog(null)}>
            <ModalDialog sx={{ overflowY: 'auto' }}>
              <React.Suspense>
                <EndVesting
                  loading={loading}
                  closeDialog={() => {
                    setDialog(null);
                  }}
                  participant={participant}
                  wpp={warrantProgram}
                  onSubmit={async formData => {
                    await updateWpp({
                      variables: {
                        companyId: company.id,
                        input: {
                          id: participant.id,
                          vestingEndDate:
                            formData.vestingEndDate?.toUTC(0, { keepLocalTime: true }).toISODate() ?? null,
                        },
                      },
                    });
                  }}
                />
              </React.Suspense>
            </ModalDialog>
          </Modal>
        </ErrorBoundary>
      )}
      {dialog === 'delete' &&
        (participant.latestAgreementDoc?.isFullySigned ? (
          <ConfirmDialog
            header={<Trans id="Are you sure you want to delete this subscriber?" />}
            confirmProps={{
              children: <Trans id="End vesting" />,
              color: 'danger',
            }}
            onCancel={() => {
              setDialog(null);
            }}
            onConfirm={async () => {
              setDialog('end-vesting');
            }}
          >
            <Trans id="It seems like this subscriber has an active agreement. Do you want to end vesting instead? You can configure the date in the next step." />
          </ConfirmDialog>
        ) : (
          <ConfirmDialog
            header={<Trans id="Are you sure you want to delete this subscriber?" />}
            confirmProps={{
              children: <Trans id="Delete" />,
              color: 'danger',
            }}
            onCancel={() => {
              setDialog(null);
            }}
            onConfirm={async () => {
              await deleteParticipant({ variables: { companyId: company.id, id: participant.id } });
              setDialog(null);
            }}
          />
        ))}
    </Card>
  );
};
